import React, { useRef, useState, useEffect } from "react";
import { photoJournalData } from "@/data/mock";
import { ChevronLeft, ChevronRight, Heart, Instagram, Youtube, Camera, Play, ExternalLink } from "lucide-react";
import axios from "axios";

const BACKEND_URL = window.__CONFIG__?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ScrollSection = ({ title, description, icon: Icon, children }) => {
  const scrollRef = useRef(null);
  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === "left" ? -340 : 340, behavior: "smooth" });
    }
  };
  return (
    <div className="mb-24">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Icon size={18} className="text-[#C4956A]" />
          <h3 className="font-bebas text-2xl tracking-[0.08em] text-white">{title}</h3>
        </div>
        <div className="flex gap-2 flex-shrink-0 mt-1">
          <button onClick={() => scroll("left")} className="w-9 h-9 flex items-center justify-center border border-white/20 text-white/40 hover:border-white/50 hover:text-white transition-all duration-300" aria-label="Scroll left"><ChevronLeft size={16} /></button>
          <button onClick={() => scroll("right")} className="w-9 h-9 flex items-center justify-center border border-white/20 text-white/40 hover:border-white/50 hover:text-white transition-all duration-300" aria-label="Scroll right"><ChevronRight size={16} /></button>
        </div>
      </div>
      {description && <p className="font-inter text-xs text-white/30 leading-relaxed max-w-xl mb-8">{description}</p>}
      <div ref={scrollRef} className="flex gap-5 overflow-x-auto scrollbar-hide pb-4">{children}</div>
    </div>
  );
};

const ContentHub = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [instagramReels, setInstagramReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ytRes, igRes] = await Promise.all([
          axios.get(`${API}/youtube/videos`),
          axios.get(`${API}/instagram/reels`),
        ]);
        setYoutubeVideos(ytRes.data.videos || []);
        setInstagramReels(igRes.data.reels || []);
      } catch (err) {
        console.error("Error fetching content hub data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const LoadingSkeleton = ({ portrait = false, count = 4 }) => (
    <>{Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`flex-shrink-0 ${portrait ? "w-[200px] aspect-[3/4]" : "w-[320px] aspect-video"} bg-[#2A2A2A] animate-pulse`} />
    ))}</>
  );

  return (
    <section id="content-hub" ref={sectionRef} className="bg-[#1A1A1A] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl text-white tracking-[0.08em]">STORIES & FRAMES</h2>
        </div>

        {/* Instagram Reels - Static Cover Images */}
        <ScrollSection title="STORIES IN MOTION" description="Short-form moments captured in motion, raw, immersive, and shaped by the energy of the journey. These are the in-between stories that don’t always make the final cut, but define the experience." icon={Instagram}>
          {[
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/o5xtplyl_image.png", url: "https://www.instagram.com/reel/DWW3NXGkwfZ/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/fpr3gyxh_image.png", url: "https://www.instagram.com/reel/DVOM75uE8zj/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/q8vr6gp7_image.png", url: "https://www.instagram.com/reel/DU8LF-6EyvF/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/47i58040_image.png", url: "https://www.instagram.com/reel/DSLK4M8kxJY/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/emfi6ch1_image.png", url: "https://www.instagram.com/reel/DO3WZl0k-ju/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/9m472tkb_image.png", url: "https://www.instagram.com/reel/DNWFgZczY89/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/wg1m3q04_image.png", url: "https://www.instagram.com/reel/DOAlvCoE0l2/" },
            { image: "https://customer-assets.emergentagent.com/job_10b7905e-b05d-40f4-bcd7-066a3acbab43/artifacts/uajz9bfd_image.png", url: "https://www.instagram.com/reel/DOSH45sE2Hd/" },
          ].map((reel, i) => (
            <a key={i} href={reel.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 w-[200px] group cursor-pointer block">
              <div className="relative overflow-hidden aspect-[3/4] bg-[#2A2A2A]">
                <img src={reel.image} alt="Instagram Reel" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Play size={18} className="text-white ml-0.5" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </ScrollSection>

        {/* YouTube Videos - Shorts (portrait) & Videos (landscape) */}
        <ScrollSection title="CINEMATIC JOURNEYS" description="Stories shaped through movement, atmosphere, and detail, capturing destinations in a way that feels immersive, intentional, and real." icon={Youtube}>
          {youtubeVideos.length > 0 ? youtubeVideos.map((video) => (
            <a key={video.video_id} href={video.link} target="_blank" rel="noopener noreferrer" className={`flex-shrink-0 group cursor-pointer block ${video.is_short ? "w-[180px]" : "w-[320px]"}`}>
              <div className={`relative overflow-hidden ${video.is_short ? "aspect-[9/16]" : "aspect-video"}`}>
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Play size={20} className="text-white ml-0.5" />
                  </div>
                </div>
                {video.is_short && <div className="absolute top-2 left-2 hidden" />}
              </div>
              <h4 className="font-inter text-xs font-medium text-white/50 mt-2 line-clamp-2 group-hover:text-white/80 transition-colors duration-300">{video.title}</h4>
            </a>
          )) : loading ? <LoadingSkeleton count={6} /> : null}
        </ScrollSection>

        {/* Photo Journal */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <Camera size={18} className="text-[#C4956A]" />
            <h3 className="font-bebas text-2xl text-white tracking-[0.08em]">THE WANDERER'S ARCHIVE</h3>
          </div>
          <p className="font-inter text-xs text-white/30 leading-relaxed max-w-xl mb-10">A collection of still moments captured along the journey, light, texture, and places as they were. Each frame holds a quiet story, preserved in time.</p>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3">
            {photoJournalData.map((item) => (
              <div key={item.id} className="overflow-hidden break-inside-avoid cursor-pointer" onClick={() => setLightboxIndex(item.id - 1)}>
                <img src={item.image} alt="Travel photo" className="w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 sm:p-8" onClick={() => setLightboxIndex(null)}>
            <button onClick={() => setLightboxIndex(null)} className="absolute top-4 right-4 sm:top-5 sm:right-5 w-10 h-10 flex items-center justify-center bg-black/50 rounded-full text-white/70 hover:text-white transition-colors z-10" aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <img
              src={photoJournalData[lightboxIndex].image}
              alt="Travel photo"
              className="max-w-full max-h-[80vh] sm:max-w-[90vw] sm:max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default ContentHub;
